import {
  Badge,
  Box,
  EditIcon,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
  Text,
  TrashIcon,
} from '@razorpay/blade/components';
import React from 'react';
import { toIndianCurrencyString } from 'utils/Numbers';
import { BonusRow } from '../hooks/types';
import { STATUS_BADGE, formatPayoutMonth, getClawbackText } from '../utils';

type Bonus = BonusRow;

type BonusTableProps = {
  bonuses: Bonus[];
  showActions: boolean;
  onEditClick?: (id: string | number) => void;
  onDeleteClick?: (id: string | number) => void;
  canEditRow?: (bonus: Bonus) => boolean;
  canDeleteRow?: (bonus: Bonus) => boolean;
};

const StatusBadge = ({ status }: { status: number | null }) => {
  const badge = status != null ? STATUS_BADGE[status.toString()] : undefined;
  if (!badge) {
    return null;
  }
  return (
    <Badge emphasis="subtle" size="medium" color={badge.color}>
      {badge.label}
    </Badge>
  );
};

export const BonusTable = ({
  bonuses,
  showActions,
  onEditClick,
  onDeleteClick,
  canEditRow,
  canDeleteRow,
}: BonusTableProps) => {
  const nodes = bonuses.map((bonus, index) => ({ ...bonus, id: bonus.id ?? `row-${index}` }));

  return (
    <Table data={{ nodes }}>
      {(tableData) => (
        <>
          <TableHeader>
            <TableHeaderRow>
              <TableHeaderCell>Component</TableHeaderCell>
              <TableHeaderCell>Payout Month</TableHeaderCell>
              <TableHeaderCell>Clawback</TableHeaderCell>
              <TableHeaderCell>Status</TableHeaderCell>
              <TableHeaderCell>
                <Box display="flex" justifyContent="flex-end">
                  Amount
                </Box>
              </TableHeaderCell>
              {showActions && <TableHeaderCell>Action</TableHeaderCell>}
            </TableHeaderRow>
          </TableHeader>
          <TableBody>
            {tableData.map((bonus) => {
              const clawback = getClawbackText(bonus);
              const canEdit = !canEditRow || canEditRow(bonus);
              const canDelete = !canDeleteRow || canDeleteRow(bonus);

              return (
                <TableRow key={bonus.id} item={bonus}>
                  <TableCell>{bonus.bonus_name ?? '-'}</TableCell>
                  <TableCell>{formatPayoutMonth(bonus.payout_month)}</TableCell>
                  <TableCell>
                    <Box display="flex" flexDirection="column">
                      <Text>{clawback.main}</Text>
                      {clawback.sub && (
                        <Text size="small" color="surface.text.gray.muted">
                          {clawback.sub}
                        </Text>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={bonus.status} />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" justifyContent="flex-end">
                      <Text>{toIndianCurrencyString(bonus.amount ?? 0, false, 2)}</Text>
                    </Box>
                  </TableCell>
                  {showActions && (
                    <TableCell>
                      <Box display="flex" gap="spacing.5">
                        {canEdit && (
                          <Link
                            icon={EditIcon}
                            onClick={() => bonus.id && onEditClick?.(bonus.id)}
                            testID={`edit-bonus-${bonus.id}`}
                          />
                        )}
                        {canDelete && (
                          <Link
                            icon={TrashIcon}
                            onClick={() => bonus.id && onDeleteClick?.(bonus.id)}
                            testID={`delete-bonus-${bonus.id}`}
                          />
                        )}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </>
      )}
    </Table>
  );
};

export default BonusTable;
